import React from 'react';
import { MapPinIcon, WifiIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface AssetStatusData {
  connected: number;
  disconnected: number;
  total: number;
}

interface AssetConnectivityMapProps {
  data: AssetStatusData;
  loading?: boolean;
}

const AssetConnectivityMap: React.FC<AssetConnectivityMapProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-48 bg-gray-200 rounded"></div>
          <div className="space-y-2">
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const connectionPercentage = data.total > 0 ? (data.connected / data.total) * 100 : 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
        <MapPinIcon className="w-5 h-5" />
        Plant Location & Asset Status
      </h3>
      
      {/* Map Placeholder */}
      <div className="relative h-48 bg-gradient-to-br from-blue-50 to-green-50 dark:from-blue-900 dark:to-green-900 rounded-lg mb-4 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPinIcon className="w-12 h-12 text-blue-600 mx-auto mb-2" />
            <div className="text-sm text-gray-600 dark:text-gray-400">Solar Plant Location</div>
            <div className="text-xs text-gray-500 dark:text-gray-500">23.0225° N, 72.5714° E</div>
          </div>
        </div>
        
        {/* Asset Status Indicators */}
        <div className="absolute top-4 right-4 space-y-2">
          {data.connected > 0 && (
            <div className="flex items-center gap-1 bg-green-100 dark:bg-green-800 px-2 py-1 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-green-700 dark:text-green-300">{data.connected} Online</span>
            </div>
          )}
          {data.disconnected > 0 && (
            <div className="flex items-center gap-1 bg-red-100 dark:bg-red-800 px-2 py-1 rounded-full">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-xs text-red-700 dark:text-red-300">{data.disconnected} Offline</span>
            </div>
          )}
        </div>
      </div>

      {/* Asset Status Summary */}
      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
          <div className="flex items-center gap-2">
            <WifiIcon className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-700 dark:text-green-300">Connected</span>
          </div>
          <span className="text-lg font-bold text-green-800 dark:text-green-200">{data.connected}</span>
        </div>

        <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/30 rounded-lg">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />
            <span className="text-sm font-medium text-red-700 dark:text-red-300">Disconnected</span>
          </div>
          <span className="text-lg font-bold text-red-800 dark:text-red-200">{data.disconnected}</span>
        </div>

        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Assets</span>
          <span className="text-lg font-bold text-gray-900 dark:text-white">{data.total}</span>
        </div>

        {/* Connection Health Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
            <span>Connection Health</span>
            <span>{connectionPercentage.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                connectionPercentage >= 80 ? 'bg-green-500' : 
                connectionPercentage >= 60 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${connectionPercentage}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetConnectivityMap;